/**
 * O catálogo de planos da gestão (#765, fase 2, #766).
 *
 * O que cada plano inclui e o preço padrão dele. Só o Super Admin dentro da
 * Cliqfy chega aqui (fase 1, #1013).
 *
 * ── A tela nasceu antes do back ───────────────────────────────────────────
 *
 * Como a lista de organizações (decisão de 26/10/2026): fica pronta, fora do
 * menu, e recebe tudo pelas props. Quem a monta consulta a API quando a rota
 * existir.
 *
 * ── O plano inativo se explica ────────────────────────────────────────────
 *
 * O "Padrão" nasce da migração, com as organizações de hoje, e fica inativo
 * para as novas. A dica no selo diz isso a quem passar o mouse ou chegar pelo
 * teclado, em vez de deixar a pessoa adivinhar por que ele não aparece na
 * criação.
 */

import type { CSSProperties, ReactNode } from 'react';
import {
  Botao,
  CabecalhoDeTela,
  Cartao,
  Dica,
  Ficha,
  Icone,
  Segmentado,
  Selo,
  type OpcaoSegmentada,
} from '@/shared/ui';
import { dinheiroComCentavos, dinheiroSemCentavosQuandoRedondo } from '@/shared/lib/formato';
import { Carregando, Erro, Vazio } from '@/shared/ui/estados/estados';
import type { PlanoDoCatalogo } from '../model/plano-do-catalogo';
import './catalogo-de-planos.css';

/** As duas partes da gestão, nas abas do topo. */
type ParteDaGestao = 'organizacoes' | 'planos';

export type TelaDePlanosProps = {
  /** O que veio do servidor; sem nada enquanto carrega. */
  planos: readonly PlanoDoCatalogo[] | undefined;
  /** O erro da consulta, quando ela falhou e não há o que mostrar. */
  erro?: unknown;
  aoRepetir: () => void;
  aoCriar: () => void;
  aoEditar: (id: string) => void;
  aoTrocarDeAba: (parte: ParteDaGestao) => void;
};

const PARTES: OpcaoSegmentada<ParteDaGestao>[] = [
  { valor: 'organizacoes', rotulo: 'Organizações' },
  { valor: 'planos', rotulo: 'Planos' },
];

const DICA_DO_INATIVO =
  'Plano inativo não aparece para organização nova. Quem já tem continua com ele.';

export function TelaDePlanos({
  planos,
  erro,
  aoRepetir,
  aoCriar,
  aoEditar,
  aoTrocarDeAba,
}: TelaDePlanosProps): ReactNode {
  return (
    <div className="catalogo-de-planos">
      <CabecalhoDeTela
        titulo="Planos"
        contagem={planos ? String(planos.length) : undefined}
        descricaoDaContagem="Planos no catálogo"
        apoio="O que cada plano inclui e quanto custa. Mudar um plano não muda o preço de quem já contratou."
        acoes={
          <Botao variante="primario" onClick={aoCriar}>
            <Icone nome="criar" />
            Novo plano
          </Botao>
        }
      />
      <div className="catalogo-de-planos__corpo">
        <Segmentado
          rotulo="Partes da gestão"
          aparencia="sublinhado"
          opcoes={PARTES}
          escolhida="planos"
          aoEscolher={aoTrocarDeAba}
        />
        <div role="tabpanel" aria-label="Planos" className="catalogo-de-planos__painel">
          <Conteudo
            planos={planos}
            erro={erro}
            aoRepetir={aoRepetir}
            aoCriar={aoCriar}
            aoEditar={aoEditar}
          />
        </div>
      </div>
    </div>
  );
}

function Conteudo({
  planos,
  erro,
  aoRepetir,
  aoCriar,
  aoEditar,
}: {
  planos: readonly PlanoDoCatalogo[] | undefined;
  erro: unknown;
  aoRepetir: () => void;
  aoCriar: () => void;
  aoEditar: (id: string) => void;
}): ReactNode {
  if (erro && !planos) return <Erro erro={erro} aoRepetir={aoRepetir} />;
  if (!planos) return <Carregando forma="cartoes" linhas={4} rotulo="Carregando os planos" />;
  if (planos.length === 0) {
    return (
      <Vazio
        titulo="Nenhum plano no catálogo"
        porque="Sem plano, a organização nova não tem com que nascer. Crie o primeiro com os usuários, o armazenamento e os preços."
        acao={{ rotulo: 'Novo plano', aoClicar: aoCriar }}
      />
    );
  }
  return (
    <div className="catalogo-de-planos__grade">
      {planos.map((plano, posicao) => (
        <CartaoDoPlano key={plano.id} plano={plano} posicao={posicao} aoEditar={aoEditar} />
      ))}
    </div>
  );
}

function CartaoDoPlano({
  plano,
  posicao,
  aoEditar,
}: {
  plano: PlanoDoCatalogo;
  posicao: number;
  aoEditar: (id: string) => void;
}): ReactNode {
  const idDoNome = `plano-${plano.id}`;
  return (
    <Cartao
      variante="folgado"
      como="article"
      parte={
        plano.ativo
          ? 'catalogo-de-planos__plano'
          : 'catalogo-de-planos__plano catalogo-de-planos__plano--inativo'
      }
      aria-labelledby={idDoNome}
      style={{ '--i': posicao } as CSSProperties}
    >
      <header className="catalogo-de-planos__cab">
        <h3 id={idDoNome} className="catalogo-de-planos__nome">
          {plano.nome}
        </h3>
        {plano.ativo ? (
          <Selo tom="ok">Ativo</Selo>
        ) : (
          <Dica texto={DICA_DO_INATIVO}>
            <Selo>Inativo</Selo>
          </Dica>
        )}
      </header>
      <p className="catalogo-de-planos__preco">
        {plano.mensalidade ? (
          <>
            <b>{dinheiroSemCentavosQuandoRedondo(plano.mensalidade)}</b> por mês
          </>
        ) : (
          <b>Legado</b>
        )}
      </p>
      <Ficha
        direita
        larguraRotulo="minmax(0, 1fr)"
        linhas={[
          { rotulo: 'Usuários incluídos', valor: plano.usuariosIncluidos ?? 'o que usava' },
          { rotulo: 'Armazenamento', valor: `${plano.armazenamentoGb} GB` },
          {
            rotulo: 'Usuário adicional',
            valor: dinheiroComCentavos(plano.usuarioAdicional) ?? 'a definir',
          },
          { rotulo: 'GB adicional', valor: dinheiroComCentavos(plano.gbAdicional) ?? 'a definir' },
        ]}
      />
      {plano.observacao ? (
        <p className="catalogo-de-planos__observacao">{plano.observacao}</p>
      ) : null}
      <footer className="catalogo-de-planos__rodape">
        <span>
          {plano.organizacoes} {plano.organizacoes === 1 ? 'organização' : 'organizações'}
        </span>
        <Botao miudo onClick={() => aoEditar(plano.id)} aria-label={`Editar o plano ${plano.nome}`}>
          Editar
        </Botao>
      </footer>
    </Cartao>
  );
}
